import {
  GetResultContext,
  FilterDefinition,
  FilterVariantDefinition,
  AppliedFilter,
} from "./utils";

export function defaultResultGetter(value: unknown, context: GetResultContext) {
  const {
    filterDefinition: { id: filterId },
    filterVariantDefinition: { id: variantId },
  } = context;
  return { [filterId]: { [variantId]: value } };
}

type FilterVariantDefinitionsTuple = [
  filterDefinition?: FilterDefinition,
  filterVariantDefinition?: FilterVariantDefinition,
];

function getDefinitionsForAppliedFilter(
  filterDefinitions: FilterDefinition[],
  appliedFilter: AppliedFilter | null | undefined,
): FilterVariantDefinitionsTuple {
  if (!appliedFilter) return [];
  const { filter, variant } = appliedFilter;
  const filterDefinition = filterDefinitions.find((fd) => fd.id === filter);
  if (!filterDefinition) return [];
  const filterVariantDefinition = filterDefinition.variants.find(
    (fvd) => fvd.id === variant,
  );
  return [filterDefinition, filterVariantDefinition];
}

type GetResultsOptions = {
  defaultResultGetter?: FilterVariantDefinition["getResult"] | null;
};

export default function getResults<R = unknown>(
  filterDefinitions: FilterDefinition[],
  appliedFilters: AppliedFilter[],
  options: GetResultsOptions = {},
) {
  if (!appliedFilters) return appliedFilters;
  const { defaultResultGetter: getResultsDefault = defaultResultGetter } =
    options;
  return appliedFilters.reduce<R[]>((acc, af, idx) => {
    const [filterDefinition, filterVariantDefinition] =
      getDefinitionsForAppliedFilter(filterDefinitions, af);

    const getResult =
      filterVariantDefinition?.getResult ||
      filterDefinition?.getResult ||
      getResultsDefault;

    if (!getResult || !filterDefinition || !filterVariantDefinition) {
      throw new Error(
        `Could not get filter result for filter ${idx + 1}: “${
          filterDefinition?.label || af.filter
        }” “${filterVariantDefinition?.label || af.variant}”`,
      );
    }
    const result = getResult(af.value, {
      filterDefinition,
      filterVariantDefinition,
    }) as R | undefined;

    if (result === undefined) return acc;

    acc.push(result);
    return acc;
  }, []);
}
